/* eslint no-cond-assign: 0 */
import {
    call,
    put,
    take,
    fork,
    select
} from 'redux-saga/effects';

import {
    ERR_MOVE_TO_WISHLIST,
    L10N_PRODUCT_WISHLIST_SUCCESS
} from '../../../core/js/enums/localizationEnums';

import { error, warn } from '../../../core/js/utils/logger';

import {
    makeRequest
} from '../../../core/js/sagas/makeRequest';

import {
    ADD_TO_WISHLIST,
    addToWishlist,
    CREATE_WISHLIST_UI,
    createWishlistUI,
    CHECK_WISHLISTS,
    addToWishListResolution,
    moveToWishListResolution,
    saveFormValidation,
    addWishlistsToUI,
    movedToWishList,
    resetAddToWishlist,
    showCannotAddToListError
} from '../actions/productActions';

import {
    removingWishlistItem,
    removedWishlistItem,
    removeWishlistItemError,
    REMOVED_WISHLIST_ITEM
} from '../../../wishlist/js/actions/wishlistActions';

import { updateWishlistItemsSaga } from '../../../wishlist/js/sagas/sagas';

import { getAddToWishlistEntity } from '../routes/productAddToCartEntities';
import { isQuickshop } from '../utils/quickshopUtils';

import {
    removeWishlistItemRoute,
    addToWishlistRoute,
    moveToWishlistRoute,
    getWishlistsRoute
} from '../../../core/js/utils/apiRoutes';

import {
    buildErrorsFromResponse
} from '../../../core/js/utils/errorHandling';

import productUtils from '../utils/utils';

import { delay } from '../../../core/js/utils';

import { config } from '../configs/config';

import { handleUtagEvent } from '@core/js/actions/initialActions.js';

export function *resetAddToWishlistAsync(ms, component) {
    yield call(delay, ms);
    yield put(resetAddToWishlist(component));
}

/* eslint-disable complexity */
/**
 * Watches for click of the add to wish list button. It will trigger a call to
 * fetch the current users wish lists (prompting for login) and then try to
 * add the currently selected product to the first wishlist on their account.
 * Based on whether the user has wishlists and it was successfully added it will message the user.
 * @returns {object} The saga effect
 */
export function *addToWishListSaga() {
    let payload = {};
    let action;

    while (action = yield take(ADD_TO_WISHLIST)) {
        payload = action.payload;
        let component;
        let state = yield select();
        let messages = {};
        let params = {};
        const isForFamilyProduct = productUtils.actionIsForFamilyProduct(action);
        const product = productUtils.getProductForAction(state, action);

        if (isForFamilyProduct) {
            component = action.meta.component;
        }

        const meta = product.productMeta;
        const sku = product.selectedSku.sku;

        try {
            if (payload.id) {
                if (sku) {
                    params = getAddToWishlistEntity(state, product);
                }

                const route = addToWishlistRoute(state, params, payload.id);

                // TODO: UW-486 Handle errors from the add to wishlist api call
                const addToWishlistResponse = yield call(makeRequest, route);

                messages.successParams = [
                    meta.displayName,
                    payload.name
                ];

                // Logic to update the count in the UI
                // Check if this SKU already exists in the Wishlist by comparing the firstAdded time with the lastModified time in the response
                const isExisting = addToWishlistResponse.firstAdded !== addToWishlistResponse.lastModified;

                if (typeof action.payload.fromDropdown !== 'undefined' && action.payload.fromDropdown && !isExisting) {
                    // Get updated wishlists
                    if (typeof product.productAddToWishList.wishlists !== 'undefined') {
                        let currentWishlists = product.productAddToWishList.wishlists;
                        const listIndex = _.findIndex(currentWishlists, ['id', payload.id]);

                        let itemToUpdate = Object.assign({}, currentWishlists[listIndex], {
                            itemCount: currentWishlists[listIndex].itemCount++
                        });

                        const updatedWishlists = [
                            ...currentWishlists.slice(0, listIndex),
                            Object.assign({}, currentWishlists[listIndex], {
                                itemCount: currentWishlists[listIndex].itemCount++
                            }),
                            ...currentWishlists.slice(listIndex + 1)
                        ];

                        yield put(addWishlistsToUI(updatedWishlists, component));
                    }
                }
            }

            yield put(handleUtagEvent({
                event_category: _.get(state, 'core.pageType'),
                event_action: 'add to wish list success',
                event_label: product.product.displayName
            }, 'link'));

            yield put(addToWishListResolution(messages, component));

            yield fork(resetAddToWishlistAsync, config.addToCart.resetDelay, component);

            // Hide the UI via CSS on successful add
            let uiSelector;

            if (component && component.templateLocation) {
                uiSelector = `${component.templateLocation} .js-product-add-links__ul`;
            } else {
                uiSelector = '.dom-main-product .js-product-add-links__ul';
            }

            $(uiSelector).hide();
        } catch (err) {
            error(`error adding to wishlist: ${err}`);
            if (err.entity) {
                const messages = buildErrorsFromResponse(err);

                if (err.entity.field_errors) {
                    yield put(saveFormValidation({}, messages, {}, component));
                }
            } else {
                warn('Unable to add to wishlist.', err);
            }
        }
    }
}
/* eslint-enable complexity */

/**
 * Get a product's Line Item ID within the context of a `cart` based on product ID
 * @param  {array} cartItems Array of objects containing data of products in current cart
 * @param  {string} productId Product ID to use as a lookup value in cartItems
 * @return {string}           The Line Item ID of the current product in the given cart
 */
export function getLineItemId(cartItems, productId) {
    for (let i = 0; i < cartItems.length; i++) {
        if (cartItems[i].productId === productId) {
            return cartItems[i].id;
        }
    }
}

/**
 * Gets a wishlist's name by its ID
 * @param  {array} wishlists  Array of current user's wishlists
 * @param  {string} wishlistId ID of wishlist to get name for
 * @return {string}            Name of wishlist based on ID
 */
export function getWishlistNameById(wishlists, wishlistId) {
    for (let i = 0; i < wishlists.length; i++) {
        if (wishlists[i].id === wishlistId) {
            return wishlists[i].name;
        }
    }
}

/**
 * Fetching the current user's wishlists.
 * This will still return results even if the user chooses not to login
 * when prompted. It will return an empty array.
 * @returns {array} An array of wishlist objects (if the user has wishlists)
 */
export function *getWishListsSaga(meta) {
    const state = yield select();
    const route = getWishlistsRoute(state);
    let res = [];

    if (typeof state.core.siteId === 'undefined') {
        warn('Unable to get wish lists: Missing siteId');
        return res;
    }

    try {
        res = yield call(makeRequest, route);
    } catch (err) {
        warn('Unable to get wish lists:', err);

        // only show error if user has not canceled out of the login modal
        if (!err.request.canceled) {
            yield put(showCannotAddToListError(isQuickshop(state), meta.component, err.code));
        }
    }

    return res;
}

/**
 * Takes the MOVE_TO_WISHLIST action and moves triggers a PUT request to move the current
 * item to the newly selected wishlist
 * @returns {object} The saga effect
 */
export function *moveToWishListSaga(params, meta) {
    let component;
    let state = yield select();
    let messages = {};
    const spoofedAction = {
        params: params,
        meta: meta
    };
    const isForFamilyProduct = productUtils.actionIsForFamilyProduct(spoofedAction);
    const product = productUtils.getProductForAction(state, spoofedAction);

    if (isForFamilyProduct) {
        component = meta.component;
    }

    const controls = product.productControls;
    const prodMeta = product.productMeta;
    const sku = product.selectedSku.sku;

    // TODO: UW-486, Handle errors from get wish lists api call
    const wishlists = yield call(getWishListsSaga, meta);

    state = yield select();
    const lineItemId = getLineItemId(state.wishlist.cart.cartItems, product.productId);
    const selectedWishlistName = getWishlistNameById(wishlists, params.id);

    if (params.id && lineItemId) {
        const route = moveToWishlistRoute(state, params, lineItemId);
        const moveToWishlistResponse = yield call(makeRequest, route);

        messages.success = L10N_PRODUCT_WISHLIST_SUCCESS;
        messages.successParams = [
            prodMeta.displayName,
            selectedWishlistName
        ];

        yield put(moveToWishListResolution(messages, component));
    } else {
        messages.error = ERR_MOVE_TO_WISHLIST;
        yield put(moveToWishListResolution(messages, component));
        throw messages.error;
    }

    yield call(updateWishlistItemsSaga);
    yield put(movedToWishList(component));
}

export function *removeFromWishlistSaga(itemKey, e) {
    yield put(removingWishlistItem());

    let state = yield select();
    const route = removeWishlistItemRoute(state, itemKey);

    try {
        let res = yield call(makeRequest, route);

        yield put(removedWishlistItem());
    } catch (err) {
        warn('Unable to delete.', err);
        yield put(removeWishlistItemError());
    }
}

/* eslint-disable complexity */
/**
 * An intermediary function to check whether to
 * display Wishlist List or to directly Add to Wishlist
 */
export function *checkWishlistsSaga() {
    let payload = {};
    let action;

    while (action = yield take(CHECK_WISHLISTS)) {
        try {
            let component;
            const wishlists = yield call(getWishListsSaga, action.meta);
            const isForFamilyProduct = productUtils.actionIsForFamilyProduct(action);

            if (isForFamilyProduct) {
                component = action.meta.component;
            }

            // If there is more than one wishlist, create the dropdown
            if (wishlists.length > 1) {
                yield put(createWishlistUI(wishlists, component));
            } else if (wishlists.length === 1) {
                // If there is only one wishlist, just add it now
                const currentWishlist = wishlists[0];
                const params = {
                    id: currentWishlist.id,
                    name: currentWishlist.name,
                    fromDropdown: false
                };

                yield put(addToWishlist(params, undefined, component));
            }
        } catch (err) {
            error(`Error checking wishlists: ${err}`);
        }
    }
}
/* eslint-enable complexity */

export function *createWishlistUISaga() {
    let payload = {};
    let action;

    while (action = yield take(CREATE_WISHLIST_UI)) {
        // Payload should be equal to list of Wishlists
        payload = action.payload;

        const isForFamilyProduct = productUtils.actionIsForFamilyProduct(action);
        let component;

        if (isForFamilyProduct) {
            component = action.meta.component;
        }

        yield put(addWishlistsToUI(payload, component));

        let uiSelector;

        if (component && component.templateLocation) {
            uiSelector = `${component.templateLocation} .js-product-add-links__ul`;
        } else {
            uiSelector = '.dom-main-product .js-product-add-links__ul';
        }

        $(uiSelector).show();
    }
}

export function *removeWishlistItemQuickshopSaga(e) {
    let payload = {};

    while ({ payload } = yield take(REMOVED_WISHLIST_ITEM)) {
        yield call(updateWishlistItemsSaga);
    }
}

export default [
    addToWishListSaga,
    createWishlistUISaga,
    checkWishlistsSaga,
    removeWishlistItemQuickshopSaga
];
